import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  LeadActivityUiModel,
  LeadCommentUiModel,
  LeadUiModel
} from '../../../shared/ui-models/lead.ui-model';
import { LeadConfigModel } from '../../../shared/models/lead.model';
import {
  addLeadComment,
  assignLead,
  changeLeadStatus,
  getLeadById,
  getLeadComments,
  getLeadConfig,
  getLeadTimeline,
  recordContactAttempt
} from '../../../shared/services/data/lead.data';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { StaffPicker } from '../../../shared/components/group-ui/staff-picker/staff-picker.component';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import { SlaBadge } from '../../shared/sla-badge.component';
import { CHIP, STATUS_STYLE, VERTICAL_STYLE, styleFor } from '../../shared/lead-visuals';
import { StatusModal } from './components/status-modal.component';

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());

/**
 * A note on the lead, whatever produced it.
 *
 * The resolution note was written into `lead.resolutionNote` and rendered
 * nowhere — someone typed why a lead was closed and it vanished. It is not a
 * comment (it belongs to the resolution, not to a conversation), so rather
 * than writing it into the comment collection it is merged here for reading
 * and labelled with where it came from.
 */
interface NoteEntry {
  id: string;
  kind: 'comment' | 'resolution';
  text: string;
  author?: string;
  at?: Date;
  /** Resolution notes carry the outcome they explain; a comment has none. */
  context?: string;
}

const NOTE_LABEL: Record<NoteEntry['kind'], { label: string; chip: string }> = {
  comment: { label: 'Comment', chip: 'bg-creams-400 text-creams-900' },
  resolution: { label: 'Resolution note', chip: 'bg-primary-100 text-primary-800' }
};

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="py-1.5">
    <div className="text-xs uppercase tracking-wide text-neutral-300">{label}</div>
    <div className="text-sm text-neutral-500">{value || '—'}</div>
  </div>
);

const Card: React.FC<{ title: string; accent?: string; children: React.ReactNode }> = ({
  title,
  accent = 'bg-primary-400',
  children
}) => (
  <div className="rounded-2xl border border-creams-600 bg-white p-5 shadow-md">
    <h2 className="mb-3 flex items-center gap-2 font-gilroy-bold text-base text-neutral-500">
      <span className={`h-4 w-1 rounded-full ${accent}`} aria-hidden="true" />
      {title}
    </h2>
    {children}
  </div>
);

export const LeadDetailsContainer: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const history = useHistory();
  const [lead, setLead] = useState<LeadUiModel | undefined>();
  const [timeline, setTimeline] = useState<LeadActivityUiModel[]>([]);
  const [comments, setComments] = useState<LeadCommentUiModel[]>([]);
  const [config, setConfig] = useState<LeadConfigModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [showStatus, setShowStatus] = useState(false);
  const [draftComment, setDraftComment] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      getLeadById(leadId),
      getLeadTimeline(leadId),
      getLeadComments(leadId),
      getLeadConfig()
    ]).then(([leadResult, timelineResult, commentsResult, configResult]) => {
      if (!isErrorInfo(leadResult)) setLead(leadResult);
      if (!isErrorInfo(timelineResult)) setTimeline(timelineResult);
      if (!isErrorInfo(commentsResult)) setComments(commentsResult);
      if (!isErrorInfo(configResult)) setConfig(configResult);
      setLoading(false);
    });
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = (
    promise: Promise<unknown>,
    messages: { loading: string; success: string; failed: string }
  ) => {
    const wrapped = promise.then((response: unknown) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      return load();
    });
    birdToast.promise(wrapped, {
      loadingMessage: messages.loading,
      successMessage: messages.success,
      errorPermissionMessage: 'You do not have permission to change this lead',
      errorFailedMessage: messages.failed
    });
  };

  const handleStatus = (payload: Record<string, unknown>) => {
    setShowStatus(false);
    run(changeLeadStatus(leadId, payload), {
      loading: 'Updating lead...',
      success: 'Lead updated',
      failed: 'Failed to update lead'
    });
  };

  const handleAttempt = () =>
    run(recordContactAttempt(leadId), {
      loading: 'Recording attempt...',
      success: 'Attempt recorded',
      // Spending the last attempt auto-resolves the lead as unreachable, and
      // any further attempt is refused until someone reopens it.
      failed: 'Could not record — the lead may be resolved already'
    });

  const handleAssign = (userId?: string) => {
    if (!userId) return;
    run(assignLead(leadId, userId), {
      loading: 'Assigning...',
      success: 'Lead assigned',
      failed: 'Failed to assign the lead'
    });
  };

  const handleComment = () => {
    const text = draftComment.trim();
    if (!text) return;
    setDraftComment('');
    run(addLeadComment(leadId, text), {
      loading: 'Adding comment...',
      success: 'Comment added',
      failed: 'Failed to add comment'
    });
  };

  if (loading && !lead) return <LoadingData />;
  if (!lead) return <NotFoundData />;

  const notes: NoteEntry[] = [
    ...comments.map((comment: LeadCommentUiModel) => ({
      id: comment.id,
      kind: 'comment' as const,
      text: comment.text,
      author: comment.authorName,
      at: comment.createdAt
    })),
    ...(lead.resolutionNote
      ? [
          {
            id: `resolution-${lead.id}`,
            kind: 'resolution' as const,
            text: lead.resolutionNote,
            at: lead.resolvedAt,
            // The note on its own does not say what it is explaining.
            context: [lead.resolutionType, lead.notQualifiedReason, lead.notInterestedReason]
              .filter(Boolean)
              .map((value) => titleCase(value as string))
              .join(' · ')
          }
        ]
      : [])
  ].sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());

  const outOfAttempts = lead.remainingContactAttempts <= 0;
  const listPath = (APP_ROUTES.LEAD_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH;

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="text-sm text-primary-500"
          onClick={() => history.push(listPath)}
        >
          ← Leads
        </button>
        <h1 className="font-gilroy-bold text-2xl text-neutral-500">
          #{lead.shortId} {lead.name || 'Unnamed lead'}
        </h1>
        <span className={`${CHIP} ${styleFor(VERTICAL_STYLE, lead.vertical)}`}>
          {titleCase(lead.vertical)}
        </span>
        <span className={`${CHIP} ${styleFor(STATUS_STYLE, lead.status)}`}>
          {titleCase(lead.status)}
        </span>
        <SlaBadge state={lead.slaState} />
        <span className="ml-auto flex gap-3">
          <Button type={ButtonType.gray} disabled={outOfAttempts} onClick={handleAttempt}>
            {outOfAttempts
              ? 'No attempts left'
              : `Log attempt (${lead.remainingContactAttempts} left)`}
          </Button>
          <Button type={ButtonType.primary} onClick={() => setShowStatus(true)}>
            Change status
          </Button>
        </span>
      </section>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5">
          <Card title="Contact" accent="bg-primary-400">
            <Field label="Phone" value={lead.phone} />
            <Field label="Email" value={lead.email} />
            <Field label="Vertical" value={titleCase(lead.vertical)} />
            <Field label="Source" value={titleCase(lead.source)} />
            <Field label="Value" value={lead.valueCategory} />
            <Field label="Message" value={lead.message} />
          </Card>

          <Card title="Assignment" accent="bg-secondary-400">
            {/* A picker, not an id field — nobody operating this has user ids. */}
            <label className={LABEL}>Assigned to</label>
            <StaffPicker
              value={lead.assignedToId}
              onChange={handleAssign}
              isClearable={false}
              placeholder="Search staff to assign"
            />
            {lead.assignedAt ? (
              <p className="mt-2 text-xs text-neutral-300">
                since {new Date(lead.assignedAt).toLocaleString()}
              </p>
            ) : (
              <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-xs text-creams-900">
                Unassigned — it stays in the queue until someone picks it up.
              </p>
            )}
          </Card>

          <Card title="Attribution" accent="bg-blue">
            <Field label="Marketing channel" value={lead.marketingChannel} />
            <Field label="Campaign" value={lead.utmCampaign} />
            <Field
              label="Source / medium"
              value={[lead.utmSource, lead.utmMedium].filter(Boolean).join(' / ')}
            />
            <Field label="First touch" value={lead.firstTouchChannel} />
          </Card>
        </div>

        <Card title="Timeline" accent="bg-primary-200">
          {timeline.length === 0 ? (
            <p className="text-sm text-neutral-400">Nothing recorded yet.</p>
          ) : (
            timeline.map((activity: LeadActivityUiModel) => (
              <div key={activity.id} className="border-t border-gray-100 py-2 first:border-t-0">
                <div className="text-sm text-neutral-500">{activity.description}</div>
                <div className="text-xs text-neutral-300">
                  {activity.isSystem ? 'System' : activity.actorName}
                  {' · '}
                  {new Date(activity.createdAt).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </Card>

        <Card title="Notes" accent="bg-creams-600">
          <textarea
            className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            rows={3}
            placeholder="Add a comment"
            value={draftComment}
            onChange={(event) => setDraftComment(event.target.value)}
          />
          <Button
            type={ButtonType.primary}
            disabled={draftComment.trim().length === 0}
            onClick={handleComment}
          >
            Comment
          </Button>

          <div className="mt-3">
            {notes.length === 0 ? (
              <p className="text-sm text-neutral-400">No notes yet.</p>
            ) : (
              notes.map((note: NoteEntry) => (
                <div key={note.id} className="border-t border-creams-500 py-2.5">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className={`${CHIP} ${NOTE_LABEL[note.kind].chip}`}>
                      {NOTE_LABEL[note.kind].label}
                    </span>
                    {note.context ? (
                      <span className="text-xs text-neutral-300">{note.context}</span>
                    ) : null}
                  </div>
                  <div className="text-sm text-neutral-500">{note.text}</div>
                  <div className="text-xs text-neutral-300">
                    {[note.author, note.at ? new Date(note.at).toLocaleString() : undefined]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {config ? (
        <StatusModal
          show={showStatus}
          lead={lead}
          config={config}
          onClose={() => setShowStatus(false)}
          onSubmit={handleStatus}
        />
      ) : null}
    </div>
  );
};
